import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ApplicationEntryLayout } from '../components/ApplicationEntryLayout/ApplicationEntryLayout';
import {
    SelectionPanelGroup,
    SelectionPanelGroupTheme,
    SelectionPanelBorderRadius,
    SelectionPanelDropShadowType,
    ProgressSpinner,
    useWaitOverlayStore,
} from '@apply/components';
import ValidationGroup from 'devextreme-react/validation-group';
import { ConfirmV2, formatCurrency, isMobileScreen } from '@apply/ui';
import '../scss/_applicationEntryLayout.scss';
import { ApplyApi, FormState, IBudgetConstruction, IBudgetFixFlip, IBudgetLand } from '@apply/api/src/api/ApplyApi';
import { applicationInsights } from '../../ApplicationInsights';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { LoanType, loanTypeDescription } from '@apply/constants/src/loans/LoanType';
import { formConstant } from '../components/FileOrFormQuestions/constants/FormConstant';
import { useSaveApplicationWithoutValidation } from '../hooks/useSaveApplicationWithoutValidation';
import { CurrentApplicationPage, useNavigateApplication } from '../hooks/useNavigateApplication';
import { budgetAndSpecSheetNameBuilder } from '../constants/budgetAndSpecSheetNameBuilder';
import { usePausePointTracker } from '../hooks/usePausePointTracker.tsx';
import { useApplicationQuery } from '../../queries/applicationQueries';
import { useApplicationPropertyForms } from '../../queries/BorrowerFields/ApplicationFields';
import { useBorrowerLoanType } from '../../queries/BorrowerFields/CoreFields';
import { usePropertyStreet1, usePropertyStreet2 } from '../../queries/usePropertyCoreFields';
import { ApplicationCrumbType } from '../components/ApplicationEntryLayout/components/ApplicationEntrySections/types/ApplicationCrumbType.tsx';

export const SelectBudget: React.FC = () => {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const { search } = useLocation();
    const propertyGuid = new URLSearchParams(search).get('pid') || '';

    const { isPending } = useApplicationQuery(entryGuid);
    const applyApi = new ApplyApi(applicationInsights);
    const navigate = useNavigate();

    const [blueRadioPanelSelections, set_blueRadioPanelSelections] = useState(['']);
    const [budgets, set_budgets] = useState<FormState[]>([]);
    const [confirmOpen, set_confirmOpen] = useState(false);
    const [pendingDelete, set_pendingDelete] = useState<null | {
        selectionId: string;
        form: FormState;
    }>(null);

    const validationGroupRef = useRef(null);
    const { saveApplicationNoValidation } = useSaveApplicationWithoutValidation();
    const { navigateApplicationForward, navigateApplicationBackward } = useNavigateApplication({
        page: CurrentApplicationPage.SelectBudget,
        entryGuid,
        propertyGuid,
    });
    const { updatePausePoint, clearPausePoint } = usePausePointTracker();
    const [loanType] = useBorrowerLoanType();
    const [propertyName] = usePropertyStreet1(propertyGuid);
    const [propertyName2] = usePropertyStreet2(propertyGuid);
    const [forms, setForms] = useApplicationPropertyForms();

    useEffect(() => {
        if (!isPending) {
            useWaitOverlayStore.getState().setWaiting(false);
        }
    }, [isPending]);

    if (isPending && entryGuid) {
        return null;
    }
    const [fullPropertyName, set_fullPropertyName] = useState('');

    const [loading, set_loading] = useState(false);

    const loadBudgets = async () => {
        if (loanType == null) return;

        const response = await applyApi.getAllBudgets(loanType!);
        if (response.statusCode !== 200) return;

        const list = response.result;

        // Filter for the budgets of the relevant loanType
        const filtered = list.filter(
            (f) => f.type !== formConstant.displayName.specSheet && f.loanType?.toString() === loanTypeDescription[loanType]
        );
        set_budgets(filtered);
    };

    useEffect(() => {
        if (loanType == null) return;

        const fetchBudgets = async () => {
            set_loading(true);
            await loadBudgets();
            set_loading(false);
        };
        fetchBudgets();
    }, [loanType]);

    useEffect(() => {
        const fullPN = budgetAndSpecSheetNameBuilder(propertyName ?? '', propertyName2 ?? '');
        set_fullPropertyName(fullPN);
    }, [propertyName, propertyName2, set_fullPropertyName]);

    const onSelectBudget = async (e: React.MouseEvent<Element, MouseEvent>) => {
        e?.preventDefault();
        const selectedId = blueRadioPanelSelections[0];
        if (!selectedId) return;

        const selectedBudget = idToBudget.get(selectedId);
        if (!selectedBudget) return;

        selectedBudget.guid = '0';
        selectedBudget.name = fullPropertyName;
        const updateSharedFormResponse = await applyApi.updateSharedForm(selectedBudget);
        selectedBudget.guid = updateSharedFormResponse.result.toString();

        clearPausePoint();

        // Update the forms array in the React Query cache with the selected budget
        const currentForms = forms ?? [];
        const filtered = currentForms.filter((f) => !(f.propertyGuid === propertyGuid && f.type === selectedBudget.type));
        setForms([...filtered, { ...selectedBudget, propertyGuid } as IBudgetConstruction | IBudgetFixFlip | IBudgetLand]);

        const response = await saveApplicationNoValidation();
        if (!response) return;

        navigateApplicationForward();
    };

    const onSaveAndExit = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        updatePausePoint();

        const applicationResponse = await saveApplicationNoValidation();
        if (!applicationResponse) return;

        navigate('/home');
    };

    const nextStepButton = {
        id: 'selectBudgetButton',
        dataTestid: 'selectBudgetButton',
        children: 'Select Budget',
        handleClick: onSelectBudget,
        disabled: !blueRadioPanelSelections[0],
    };

    const saveAndExitButton = {
        id: 'saveAndExitApplicationButton',
        dataTestid: 'saveAndExitApplicationButton',
        children: 'Save and Exit',
        handleClick: onSaveAndExit,
    };

    const onDeleteBudget = useCallback(async (selectionId: string, form: FormState, e?: React.MouseEvent<Element>) => {
        e?.preventDefault();
        e?.stopPropagation();
        if (!form.id) return;

        set_pendingDelete({ selectionId, form });
        set_confirmOpen(true);
    }, []);

    const onConfirmDelete = useCallback(async () => {
        if (!pendingDelete) return;
        const { selectionId, form } = pendingDelete;

        if (!form.id || form.type == null) {
            set_confirmOpen(false);
            set_pendingDelete(null);
            return;
        }

        const response = await applyApi.deleteSavedForm(form.id, form.type);
        if (response.statusCode !== 200) {
            set_confirmOpen(false);
            set_pendingDelete(null);
            return;
        }

        set_budgets((prev) => prev.filter((f) => f.id !== form.id));
        set_blueRadioPanelSelections((prev) => (prev[0] === selectionId ? [''] : prev));

        set_confirmOpen(false);
        set_pendingDelete(null);
    }, [pendingDelete, applyApi, set_budgets, set_blueRadioPanelSelections]);

    const onCancelDelete = useCallback(() => {
        set_confirmOpen(false);
        set_pendingDelete(null);
    }, []);

    const renderOption = (form: FormState, id: string, details: string, showIcon: boolean) => {
        return (
            <>
                <div className='item'>
                    <div className='itemContent'>
                        <div className='itemTitle'>{form.name}</div>
                        <div className='itemSubtitle'>{details}</div>
                    </div>
                    {showIcon && (
                        <div
                            id={`deleteOption_${id}`}
                            className='itemTrash'
                            onClick={(e) => onDeleteBudget(id, form, e)}
                        >
                            <FontAwesomeIcon
                                color='red'
                                fontSize='20px'
                                icon={faTrashCan}
                            />
                        </div>
                    )}
                </div>
            </>
        );
    };

    const { radioOptions, idToBudget } = useMemo(() => {
        const map = new Map<string, FormState>();
        const options = budgets.map((budget) => {
            const selectionId = `${budget.guid}`;
            map.set(selectionId, budget);
            const landCost = Number(budget.values?.['Land Cost']?.['Land Cost'] ?? 0);

            return {
                id: selectionId,
                children: renderOption(
                    budget,
                    selectionId,
                    `${formatCurrency(landCost)} + ${budget.loanType}`,
                    isMobileScreen() && !blueRadioPanelSelections[0]
                ),
                handleClick: (e: React.MouseEvent<Element>) => {
                    e.preventDefault();
                    set_blueRadioPanelSelections([selectionId]);
                },
            };
        });
        return { radioOptions: options, idToBudget: map };
    }, [budgets, blueRadioPanelSelections.length]);

    const previousStepButtonClick = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        clearPausePoint();

        const applicationResponse = await saveApplicationNoValidation();
        if (!applicationResponse) return;

        navigateApplicationBackward();
    };

    return (
        <ValidationGroup ref={validationGroupRef}>
            <ApplicationEntryLayout
                currentCrumb={ApplicationCrumbType.Budget}
                saveAndExitButtonProps={saveAndExitButton}
                nextStepButtonProps={nextStepButton}
                previousStepButtonVisible={true}
                previousStepButtonClick={previousStepButtonClick}
                propertyAddress={propertyName}
                overrideDefaultContentWidth={true}
            >
                <>
                    <h2 style={{ marginBottom: '3rem', marginTop: '2rem' }}>Previously Saved Budgets</h2>
                    <div>
                        {loading && <ProgressSpinner />}
                        {!loading && (
                            <SelectionPanelGroup
                                id='budgetRadioGroup'
                                items={radioOptions}
                                theme={SelectionPanelGroupTheme.Blue}
                                selectedPanels={blueRadioPanelSelections}
                                set_selectedPanels={set_blueRadioPanelSelections}
                                borderRadius={SelectionPanelBorderRadius.Small}
                                dropShadowType={SelectionPanelDropShadowType.Small}
                                wrapPanels={isMobileScreen() ? true : false}
                            />
                        )}
                    </div>
                    <ConfirmV2
                        id='deleteSavedFormsConfimModal'
                        title='Delete Budget'
                        okLabel='Yes'
                        cancelLabel='No'
                        open={confirmOpen}
                        onConfirm={onConfirmDelete}
                        onCancel={onCancelDelete}
                        height='200px'
                        width='400px'
                    >
                        Are you sure you want to delete this Budget?
                    </ConfirmV2>
                </>
            </ApplicationEntryLayout>
        </ValidationGroup>
    );
};
